export const HAUFFE_CUSTOMER_VALUE_METHOD_VERSION = "HAUFFE-CV-METHOD-1.0";

export const HAUFFE_CUSTOMER_VALUE_METHOD_NOTE =
  "Diese Customer Value Calculation basiert auf der HAUFFE OS Methodik und den eingereichten Abteilungsperspektiven. Sie ist ein vorbereiteter Arbeitsstand und muss final durch den Company Owner geprüft werden.";

export const HAUFFE_AI_SYNTHESIS_METHOD_NOTE =
  "Diese Auswertung basiert auf der HAUFFE OS Methodik und den eingereichten Abteilungsperspektiven. Sie ist ein vorbereiteter Arbeitsstand und muss final durch den Company Owner geprüft werden.";

export const HAUFFE_CUSTOMER_VALUE_METHOD_NOTE_EN =
  "This Customer Value Calculation is based on the HAUFFE OS methodology and the submitted perspectives. It is a prepared working state and must be reviewed by the Company Owner before final use.";

export const HAUFFE_AI_SYNTHESIS_METHOD_NOTE_EN =
  "This evaluation is based on the HAUFFE OS methodology and the submitted department perspectives. It is a prepared working state and must be reviewed by the Company Owner.";

export const HAUFFE_CUSTOMER_VALUE_METHODOLOGY = {
  methodVersion: HAUFFE_CUSTOMER_VALUE_METHOD_VERSION,
  principles: [
    "Customer Value bewertet die Passung einer kommerziellen Beziehung zur organisationalen Entscheidungslogik, nicht den Kunden als Person.",
    "HAUFFE OS liefert die Methodik; die Organisation prueft und finalisiert Kriterien, Gewichtungen und Segmentlogik.",
    "Kriterien muessen aus beobachtbarer Evidenz, Abteilungsperspektiven und strategischer Passung ableitbar sein.",
    "Gewichtungen sind Vorschlaege fuer einen Arbeitsstand und werden durch den Company Owner final geprueft.",
    "Negative Aufwandssignale koennen den Customer Value Score reduzieren, auch wenn Umsatz vorhanden ist."
  ],
  criterionGroups: [
    {
      name: "Strategische Passung",
      description: "Wie stark der Kunde zur Richtung, Positionierung und gewuenschten Entwicklung der Organisation passt.",
      type: "positive",
      suggestedWeight: 25
    },
    {
      name: "Wirtschaftlicher Beitrag",
      description: "Wie klar Umsatz, Marge, Planbarkeit und Zahlungsqualitaet zu tragfaehigem Wert beitragen.",
      type: "positive",
      suggestedWeight: 20
    },
    {
      name: "Wiederkehrender Bedarf",
      description: "Wie wahrscheinlich wiederholbarer Bedarf, Ausbaupotenzial oder langfristige Zusammenarbeit ist.",
      type: "positive",
      suggestedWeight: 15
    },
    {
      name: "Operativer Aufwand",
      description: "Wie stark Sonderwuensche, Koordination, Lieferkomplexitaet oder interne Reibung Kapazitaet binden.",
      type: "negative",
      suggestedWeight: 20
    },
    {
      name: "Entscheidungsklarheit",
      description: "Wie klar Prioritaeten, Verantwortlichkeiten, Entscheidungswege und Erwartungshaltung im Kundenkontext sind.",
      type: "positive",
      suggestedWeight: 20
    }
  ],
  weightingPrinciples: [
    "Die Summe aktiver Gewichtungen soll 100 ergeben.",
    "Strategische Passung und wirtschaftlicher Beitrag erhalten nur dann hohes Gewicht, wenn sie durch Abteilungsperspektiven gestuetzt werden.",
    "Aufwand und Reibung duerfen explizit negativ gewichtet werden.",
    "Gewichtungen bleiben Vorschlaege, bis der Company Owner sie prueft."
  ],
  scoreLogic: {
    scale: "Jedes Kriterium wird von 1 bis 5 bewertet.",
    positiveCriteria: "Bei positiven Kriterien bedeutet ein hoher Wert hoehere Passung.",
    negativeCriteria: "Bei negativen Kriterien bedeutet ein hoher Wert hoeheren Aufwand und reduziert den Score.",
    normalizedScore: "Der Customer Value Score wird als normalisierter Wert von 0 bis 100 berechnet."
  },
  segmentLogic: [
    { segmentName: "Höchste Wertspanne", minScore: 80, maxScore: 100 },
    { segmentName: "Starke Wertspanne", minScore: 70, maxScore: 79 },
    { segmentName: "Entwicklungsspanne", minScore: 60, maxScore: 69 },
    { segmentName: "Prüfspanne", minScore: 45, maxScore: 59 },
    { segmentName: "Niedrige Kundenwertspanne", minScore: 0, maxScore: 44 }
  ],
  crmFieldSuggestions: [
    "customer_value_score",
    "customer_value_segment",
    "customer_fit_score",
    "customer_effort_score",
    "customer_value_recommendation",
    "customer_value_review_status"
  ],
  csvColumnSuggestions: [
    "customer_name",
    "current_segment",
    "revenue_estimate",
    "margin_estimate",
    "criterion_score",
    "criterion_weight",
    "customer_value_score",
    "customer_value_segment",
    "recommendation",
    "notes"
  ],
  finalReviewRequired: HAUFFE_CUSTOMER_VALUE_METHOD_NOTE
} as const;

export function getHauffeCustomerValueMethodNote(language: "de" | "en" = "de"): string {
  return language === "en" ? HAUFFE_CUSTOMER_VALUE_METHOD_NOTE_EN : HAUFFE_CUSTOMER_VALUE_METHOD_NOTE;
}

export function getHauffeAiSynthesisMethodNote(language: "de" | "en" = "de"): string {
  return language === "en" ? HAUFFE_AI_SYNTHESIS_METHOD_NOTE_EN : HAUFFE_AI_SYNTHESIS_METHOD_NOTE;
}

const HAUFFE_CUSTOMER_VALUE_METHODOLOGY_EN = {
  methodVersion: HAUFFE_CUSTOMER_VALUE_METHOD_VERSION,
  principles: [
    "Customer Value evaluates the fit of a commercial relationship with the organization's decision logic, not the customer as a person.",
    "HAUFFE OS provides the methodology; the organization reviews and finalizes criteria, weighting and segment logic.",
    "Criteria must be derived from observable evidence, department perspectives and strategic fit.",
    "Weightings remain suggestions until the Company Owner reviews them.",
    "Negative effort signals can reduce the Customer Value Score even when revenue exists."
  ],
  criterionGroups: [
    {
      name: "Strategic Fit",
      description: "How strongly the customer fits the organization's direction, positioning and desired development.",
      type: "positive",
      suggestedWeight: 25
    },
    {
      name: "Economic Contribution",
      description: "How clearly revenue, margin, predictability and payment quality contribute to sustainable value.",
      type: "positive",
      suggestedWeight: 20
    },
    {
      name: "Recurring Need",
      description: "How likely repeatable demand, expansion potential or long-term cooperation is.",
      type: "positive",
      suggestedWeight: 15
    },
    {
      name: "Operational Effort",
      description: "How strongly exceptions, coordination, delivery complexity or internal friction bind capacity.",
      type: "negative",
      suggestedWeight: 20
    },
    {
      name: "Decision Clarity",
      description: "How clear priorities, responsibilities, decision paths and expectations are in the customer context.",
      type: "positive",
      suggestedWeight: 20
    }
  ],
  weightingPrinciples: [
    "The sum of active weightings should equal 100.",
    "Strategic fit and economic contribution should receive high weight only when supported by department perspectives.",
    "Effort and friction may be explicitly weighted negatively.",
    "Weightings remain suggestions until the Company Owner reviews them."
  ],
  scoreLogic: {
    scale: "Each criterion is scored from 1 to 5.",
    positiveCriteria: "For positive criteria, a high value means stronger fit.",
    negativeCriteria: "For negative criteria, a high value means higher effort and reduces the score.",
    normalizedScore: "The Customer Value Score is calculated as a normalized value from 0 to 100."
  },
  segmentLogic: [
    { segmentName: "Highest Value Range", minScore: 80, maxScore: 100 },
    { segmentName: "Strong Value Range", minScore: 70, maxScore: 79 },
    { segmentName: "Development Range", minScore: 60, maxScore: 69 },
    { segmentName: "Review Range", minScore: 45, maxScore: 59 },
    { segmentName: "Low Customer Value Range", minScore: 0, maxScore: 44 }
  ],
  crmFieldSuggestions: HAUFFE_CUSTOMER_VALUE_METHODOLOGY.crmFieldSuggestions,
  csvColumnSuggestions: HAUFFE_CUSTOMER_VALUE_METHODOLOGY.csvColumnSuggestions,
  finalReviewRequired: HAUFFE_CUSTOMER_VALUE_METHOD_NOTE_EN
} as const;

export function getHauffeCustomerValueMethodologyForPrompt(language: "de" | "en" = "de") {
  const methodology = language === "en" ? HAUFFE_CUSTOMER_VALUE_METHODOLOGY_EN : HAUFFE_CUSTOMER_VALUE_METHODOLOGY;
  return {
    method_version: methodology.methodVersion,
    principles: methodology.principles,
    criterion_groups: methodology.criterionGroups,
    weighting_principles: methodology.weightingPrinciples,
    score_logic: methodology.scoreLogic,
    segment_logic: methodology.segmentLogic,
    crm_field_suggestions: methodology.crmFieldSuggestions,
    csv_column_suggestions: methodology.csvColumnSuggestions,
    final_review_required: methodology.finalReviewRequired
  };
}
